<template>
    <div id="step1">
        <div class="text-center text-xl my-12" v-if="loading">
            <font-awesome-icon spin icon="spinner" size="2x"></font-awesome-icon>
            <p>Please wait while we find your eligible yearbook photos...</p>
            <p v-if="loadTime > 30 && loadTime < 70">Finishing up...</p>
            <div v-else-if="loadTime > 70">
                There may have been an error. Please refresh the page and try again.
            </div>
        </div>

        <div v-else>
            <div v-if="order_data.poses && order_data.poses.length > 0">
                <HeadFoot title="Yearbook Pose" :disableBack="true">
                    <button type="button" class="font-medium transition-all duration-300 border-2 rounded-md inline-block py-2 px-3 md:px-4 bg-leonard-green border-leonard-green text-leonard-white hover:bg-leonard-green-hover hover:border-transparent focus:ring-leonard-green cursor-pointer disabled:bg-leonard-green-disabled disabled:text-leonard-green disabled:cursor-not-allowed" aria-label="Continue to Next Step" @click="next()">
                        Continue <font-awesome-icon :icon="['fas', 'arrow-right']" class="ml-1"></font-awesome-icon>
                    </button>
                </HeadFoot>

                <div v-if="order_data.poses[0].ybkcutoff !== null">
                    <div v-if="school_name" class="font-bold text-center text-lg">{{school_name}}</div>
                    <!-- Yearbook Cutoff Alert -->
                    <Alert align="left" type="warning" title="Yearbook Pose Selection Ended" width="50" v-if="pastCutoff">
                        The cutoff date has passed: <span class="font-bold">{{cutoffDate}}</span>. No changes are allowed.
                    </Alert>
                    <Alert align="left" title="Cutoff Date" type="info" width="50" v-if="!pastCutoff">
                        No Yearbook Pose changes allowed after: <strong v-if="order_data.poses.length > 0">{{cutoffDate}}</strong>
                    </Alert>

                    <div class="text-3xl text-black font-bold text-center my-8" v-if="!pastCutoff">Click on the photo to change it</div>

                    <div class="flex flex-wrap items-top justify-around mb-6">
                        <div class="w-52 relative" :class="{'clickable': !pastCutoff, 'unclickable': pastCutoff}" :data-toggle="!pastCutoff ? 'modal' : ''" :data-target="!pastCutoff ? '#yearbook_pose' : ''" @click="openChangePose('formal')">
                            <div class="font-bold text-center text-lg">Formal Pose</div>
                            <div class="text-sm text-orange-500 font-medium text-center" v-if="!pastCutoff && !formalPose.set">Choose a selection now!</div>
                            <figure class="w-full relative">
                                <img :src="formalPose.image_path || '/images/default.png'" alt="Formal Pose" class="w-full border-4">
                                <button type="button" class="absolute -top-2 -right-2 rounded-full w-8 h-8 text-black shadow-sm" :class="{'bg-yellow-200': formalPose.set, 'bg-green-400': !formalPose.set}" v-if="!pastCutoff" aria-label="Change Formal Pose">
                                    <font-awesome-icon fixed-width :icon="['fas', 'arrow-right-arrow-left']" class="h-4 w-4" v-if="formalPose.set"></font-awesome-icon>
                                    <font-awesome-icon fixed-width :icon="['fas', 'plus']" class="h-4 w-4" v-else></font-awesome-icon>
                                </button>
                                <div class="absolute bottom-3 w-full text-center text-white font-bold leading-none text-shadow" v-if="formalPose.set">{{parseInt(formalPose.FLM_ORD_NO).toString().slice(-2) + '-' + parseInt(formalPose.FRM).toString().padStart(3, '0')}}</div>
                            </figure>
                            <div v-if="!pastCutoff && formalPose.set && isEmployee">
                                <div class="text-center italic text-sm" v-if="formalPose.student">Showing Student Selection</div>
                                <div class="text-center italic text-sm" v-if="!formalPose.student">Showing Leonards Selection</div>
                            </div>
                            <div class="text-sm text-red-600 font-medium text-center" v-if="pastCutoff && !formalPose.set">No image was selected before the cutoff date. An image will be chosen for you by our lab professionals.</div>
                        </div>

                        <div v-if="casualPose.show" class="w-52 relative" :class="{'clickable': !pastCutoff, 'unclickable': pastCutoff}" :data-toggle="!pastCutoff ? 'modal' : ''" :data-target="!pastCutoff ? '#yearbook_pose' : ''" @click="openChangePose('casual')">
                            <div class="font-bold text-center text-lg">Casual Pose</div>
                            <figure class="w-full relative">
                                <img :src="casualPose.image_path || '/images/default.png'" alt="Casual Pose" class="w-full">
                                <button type="button" class="absolute -top-2 -right-2 rounded-full w-8 h-8 text-black shadow" :class="{'bg-yellow-200': casualPose.image_path, 'bg-green-400': !casualPose.image_path}" v-if="!pastCutoff" aria-label="Change Casual Pose">
                                    <font-awesome-icon fixed-width :icon="['fas', 'arrow-right-arrow-left']" class="h-4 w-4" v-if="casualPose.image_path"></font-awesome-icon>
                                    <font-awesome-icon fixed-width :icon="['fas', 'plus']" class="h-4 w-4" v-else></font-awesome-icon>
                                </button>
                                <div class="absolute bottom-3 w-full text-center text-white font-bold leading-none text-shadow" v-if="casualPose.image_path">{{parseInt(casualPose.FLM_ORD_NO).toString().slice(-2) + '-' + parseInt(casualPose.FRM).toString().padStart(3, '0')}}</div>
                            </figure>
                            <div v-if="!pastCutoff && casualPose.set && isEmployee">
                                <div class="text-center italic text-sm" v-if="casualPose.student">Showing Student Selection</div>
                                <div class="text-center italic text-sm" v-if="!casualPose.student">Showing Leonards Selection</div>
                            </div>
                            <div class="text-sm text-orange-500 font-medium text-center" v-if="!pastCutoff && !casualPose.image_path">No selection yet!</div>
                            <div class="text-sm text-red-600 font-medium text-center" v-if="pastCutoff && !casualPose.image_path">No image was selected before the cutoff date. An image will be chosen for you by our lab professionals.</div>
                        </div>
                    </div>
                </div>

                <HeadFoot v-if="order_data.poses[0].ybkcutoff !== null" :disableBack="true">
                    <button type="button" class="font-medium transition-all duration-300 border-2 rounded-md inline-block py-2 px-3 md:px-4 bg-leonard-green border-leonard-green text-leonard-white hover:bg-leonard-green-hover hover:border-transparent focus:ring-leonard-green cursor-pointer disabled:bg-leonard-green-disabled disabled:text-leonard-green disabled:cursor-not-allowed" aria-label="Continue to Next Step" @click="next()">
                        Continue <font-awesome-icon :icon="['fas', 'arrow-right']" class="ml-1"></font-awesome-icon>
                    </button>
                </HeadFoot>
            </div>

            <div v-else>
                <HeadFoot title="Yearbook Pose" :disableBack="true">
                    <button type="button" class="font-medium transition-all duration-300 border-2 rounded-md inline-block py-2 px-3 md:px-4 bg-leonard-green border-leonard-green text-leonard-white hover:bg-leonard-green-hover hover:border-transparent focus:ring-leonard-green cursor-pointer disabled:bg-leonard-green-disabled disabled:text-leonard-green disabled:cursor-not-allowed" aria-label="Continue to Next Step" @click="next()">
                        Continue <font-awesome-icon :icon="['fas', 'arrow-right']" class="ml-1"></font-awesome-icon>
                    </button>
                </HeadFoot>
                <Alert align="left" title="Pose Not Found!" type="danger" width="50">
                    We are unable to find a yearbook pose connected to the student. Please contact the studio to ensure that your yearbook pose is available for review.
                </Alert>
            </div>

            <!-- Modal for changing yearbook pose -->
            <YearbookPoseModal
                :hasFormalRetouching="hasFormalRetouching"
                :hasCasualRetouching="hasCasualRetouching"
                :pastCutoff="pastCutoff"
                :change="change"
                v-on:savePose="updatePose"
                v-if="change.available.length > 0"
            ></YearbookPoseModal>
        </div>
    </div>
</template>

<script>
    import { library } from '@fortawesome/fontawesome-svg-core';
    import { faPlus, faArrowRight, faArrowRightArrowLeft, faSpinner } from '@fortawesome/free-solid-svg-icons';

    import { lightFormat, parse, isAfter, getYear, getMonth, endOfDay } from 'date-fns';
    import Cart from '../../classes/Cart';
    import Order from '../../classes/Order';
    import Photo from '../../classes/Photo';
    import Setting from '../../classes/Setting';

    import Alert from '../../components/app/Alert';
    import HeadFoot from '../ordersteps/partials/StepHeaderFooter';
    import YearbookPoseModal from '../ordersteps/partials/YearbookPoseModal';

    export default {
        data() {
            return {
                Cart: new Cart(),
                Order: new Order(),
                Photo: new Photo(),
                Setting: new Setting(),
                access_code: localStorage.getItem('access_code'),
                processing: null,
                school_name:null,
                order_data: {},
                change: {
                    type: 'formal',
                    available: [],
                    current: {}
                },
                casualPose: {
                    show: false,
                    src: '',
                    flm: '',
                    frm: '',
                },
                formalPose: {
                    set: false,
                    src: '',
                    flm: '',
                    frm: '',
                },
                pastCutoff: false,
                hasFormalRetouching: false,
                hasCasualRetouching: false,
                loading: false,
                loadTime: 0,
                timer: null,
                isAfter, lightFormat, parse, getYear, getMonth, endOfDay
            }
        },

        components: {
            Alert,
            HeadFoot,
            YearbookPoseModal,
        },

        created() {
            library.add(faSpinner, faArrowRight, faArrowRightArrowLeft, faPlus);

            if (this.access_code) {
                this.checkPhotos();
                this.checkCart();
            }

            window.events.$on('confirmed', (context) => {
                if (context === 'Yearbook') {
                    this.$emit('nextStep');
                }
            });
        },

        mounted() {
            window.events.$emit('updateProgress')
            document.title = 'Yearbook Pose - ' + document.title;
        },

        methods: {
            checkPhotos() {
                let order = new Order();
                let data = {
                    access_code: this.access_code,
                    user_id: this.$auth.user().details.id,
                    yb_eligible: true
                };

                window.events.$emit('finishedLoading');

                this.startTimer()
                order.checkPhotos(data).then((response) => {
                    let data = response.data ?? null;

                    if (response['success'] === true) {
                        const photos = Array.isArray(data.photos) ? data.photos : Object.values(data.photos);

                        if (photos.length === 0) {
                            this.pastCutoff = true;
                            this.order_data = {
                                poses: photos
                            };
                        } else {
                            this.school_name = photos[0].school_name;
                            localStorage.setItem('con_no', photos[0].CON_NO);

                            if(photos[0].ybkcutoff !== null) {
                                let ybkcutoff = this.validDate(photos[0].ybkcutoff);
                                if(this.isAfter(new Date(), this.endOfDay(Date.parse(ybkcutoff)))) {
                                    this.pastCutoff = true;
                                }
                            }

                            this.order_data = {
                                poses: photos
                            };

                            this.setFormalPose(photos);
                            this.setCasualPose(photos);
                        }
                    }
                }).catch((error) => {
                    console.error('error', error);
                    feedback('An error occurred while retrieving the student yearbook details.', 'danger');
                    this.$router.push({params: {step: null}});
                }).finally(() => {
                    this.endTimer()
                });
            },

            checkCart() {
                let cart = new Cart();

                cart.getCart(this.$auth.user().details.id).then((response) => {
                    let items = response.data.items;
                    if(items.length > 0) {
                        let formal = items.filter(item => item.name === 'Formal Pose Retouching')[0]
                        let casual = items.filter(item => item.name === 'Casual Pose Retouching')[0]
                        if(formal) {
                            this.hasFormalRetouching = formal
                        }
                        if(casual) {
                            this.hasCasualRetouching = casual
                        }

                    }
                })
            },

            startTimer() {
                this.loading = true;

                let start = Date.now();
                this.timer = setInterval(() => {
                    let delta = Date.now() - start; // milliseconds elapsed since start
                    this.loadTime = Math.floor(delta / 1000); // in seconds
                }, 1000);
            },

            endTimer() {
                this.loading = false;
                clearInterval(this.timer);
                this.loadTime = 0;
            },

            validDate(d) {
                //let parsed = this.parse(d, 'MM/dd/yyyy hh:mm:ss a', new Date());
                let parsed = this.parse(d, 'yyyy-MM-dd HH:mm:ss', new Date());

                if(parsed instanceof Date && isNaN(parsed.getTime())) {
                    console.warn("Invalid date, attempting to reparse...");
                    parsed = this.parse(d, 'yyyy-MM-dd', new Date());
                }

                return parsed;
            },

            setFormalPose(photos) {
                photos.some((photo) => {
                    let poseObj = {
                        set: true,
                        image_path: photo.image_path,
                        FLM_ORD_NO: photo.FLM_ORD_NO,
                        FRM: photo.FRM,
                        student: false,
                    };

                    if(photo.YBE1 === 1 && photo.YBKPOSE2 === 1) {
                        this.formalPose = poseObj;
                    }

                    if(photo.YBE1 === 1 && photo.YBKPOSE === 1) { // Prioritize Student Selection
                        poseObj.student = true;
                        this.formalPose = poseObj;

                        return true;
                    }
                });
            },

            setCasualPose(photos) {
                const poseObj = {
                    show: false,
                    image_path: null,
                    FLM_ORD_NO: null,
                    FRM: null,
                    student: false,
                };

                photos.some((photo) => {
                    if(photo.YBE2 === 1) {
                        poseObj.show = true;
                    }

                    if(photo.YBE2 === 1 && photo.YBKPOSE4 === 1) {
                        poseObj.image_path = photo.image_path
                        poseObj.FLM_ORD_NO = photo.FLM_ORD_NO
                        poseObj.FRM = photo.FRM
                        poseObj.student = false;
                    }

                    if(photo.YBE2 === 1 && photo.YBKPOSE3 === 1) {
                        // prioritize Student Selection
                        poseObj.image_path = photo.image_path
                        poseObj.FLM_ORD_NO = photo.FLM_ORD_NO
                        poseObj.FRM = photo.FRM
                        poseObj.student = true;
                        return true;
                    }
                });

                this.casualPose = poseObj;
            },

            openChangePose(type) {
                if(this.pastCutoff) {
                    feedback('You cannot change poses after the cutoff date.', 'danger');
                    return
                }

                let filter = (type === 'casual') ? 'YBE2' : 'YBE1';
                let availablePoses = [];

                this.order_data.poses.forEach(function(photo) {
                    if(photo[filter]) {
                        availablePoses.push(photo);
                    }
                });

                let currentPose = (type === 'casual') ? this.casualPose : this.formalPose;

                this.change = {
                    type: type,
                    available: availablePoses,
                    current: currentPose
                }
            },

            updatePose(poseType, changedPose, ybk) {
                let poses = this.order_data.poses;
                let index = poses.findIndex(pose => pose.FLM_ORD_NO === changedPose.FLM_ORD_NO && pose.FRM === changedPose.FRM);
                let ybkpose = this.isEmployee ? ybk[1] : ybk[0];

                if(index !== -1) {
                    poses.forEach(pose => {
                        pose[ybkpose] = 0;
                    });

                    poses[index][ybkpose] = 1;

                    // only change pose if there is not a student selection
                    // or this is not an employee
                    if(poseType === 'casual' && (!this.isEmployee || this.isEmployee && !this.casualPose.student)) {
                        this.casualPose = {
                            show: true,
                            image_path: changedPose.image_path,
                            FLM_ORD_NO: changedPose.FLM_ORD_NO,
                            FRM: changedPose.FRM
                        }
                    }

                    if(poseType === 'formal' && (!this.isEmployee || this.isEmployee && !this.formalPose.student)) {
                        this.formalPose = {
                            set: true,
                            image_path: changedPose.image_path,
                            FLM_ORD_NO: changedPose.FLM_ORD_NO,
                            FRM: changedPose.FRM
                        }
                    }
                }
            },

            isFormal: function(pose) {
                return pose.FRM === this.formalPose.FRM && pose.FLM_ORD_NO === this.formalPose.FLM_ORD_NO
            },

            isCasual: function(pose) {
                return pose.FRM === this.casualPose.FRM && pose.FLM_ORD_NO === this.casualPose.FLM_ORD_NO
            },

            adminSelection: function(pose, type) {
                return ((type === 'formal' && parseInt(pose.YBKPOSE2) === 1) || (type === 'casual' && parseInt(pose.YBKPOSE4) === 1)) && this.isEmployee;
            },

            studentSelection: function(pose, type) {
                return ((type === 'formal' && parseInt(pose.YBKPOSE) === 1) || (type === 'casual' && parseInt(pose.YBKPOSE3) === 1));
            },

            checkSelection() {
                if(
                    (!this.formalPose.set || (this.casualPose.show && this.casualPose.FLM_ORD_NO === null))
                    && this.order_data.poses && this.order_data.poses.length > 0
                ) {
                    window.events.$emit('showConfirmation', {
                        show: true,
                        header: 'Wait!',
                        body: `You're about to skip the yearbook pose selections. Are you sure you want to continue?`,
                        cancelText: 'Go Back',
                        confirmText: 'Continue',
                        context: 'Yearbook'
                    });

                    return false;
                }

                return true;
            },

            next() {
                // Verify that poses have been selected
                if(!this.checkSelection()) {
                    // this.$emit('nextStep');
                    console.log('no selection made, show modal')
                    return;
                }

                this.$emit('nextStep');
            }
        },

        computed: {
            cutoffDate: function() {
                let date = this.validDate(this.order_data.poses[0].ybkcutoff);
                return this.lightFormat(date, 'MM/dd/yyyy');
            }
        }
    }
</script>

<style lang="scss" scoped>
    .text-shadow {
        text-shadow: 0 0 4px #000;
    }

    .btn[disabled], .btn.disabled {
        cursor: not-allowed;
    }

    .clickable {
        cursor: pointer;
    }
    .unclickable {
        cursor: not-allowed;
    }

    figure {
        img {
            border-radius: 1rem;
        }

        small {
            position: absolute;
            bottom: -20px;
            left: 0;
            right: 0;
            text-align: center;
        }
    }

    @media screen and (min-width: 991.98px) {
        figure {
            margin-top: 0;

            img {
                width: 100%;
            }
        }
    }
</style>
